import { useCallback, useEffect, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut } from "lucide-react";
import { API_BASE } from "@/lib/site";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LeadDrawer from "@/pages/admin/LeadDrawer";

/* Standalone lead detail page rendered at /admin/leads/:id.
 * Reuses LeadDrawer's tab content with mode="page" so all overview/activity/
 * reminders/files functionality is identical to the drawer. */
export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [lead, setLead] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  const fetchLead = useCallback(async () => {
    setFetching(true);
    try {
      const r = await axios.get(`${API_BASE}/admin/leads/${id}`, { withCredentials: true });
      setLead(r.data);
      setError(null);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login");
        return;
      }
      setError(err?.response?.status === 404 ? "Lead not found." : "Could not load lead.");
    } finally {
      setFetching(false);
    }
  }, [id, navigate]);

  // Lazy-fetch other leads only for the duplicate-warning panel link-outs.
  const fetchOthers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/admin/leads`, {
        params: { view: "active", limit: 500 },
        withCredentials: true,
      });
      setAllLeads(r.data);
    } catch (err) { console.warn("[lead-detail] could not load sibling leads (non-blocking):", err); }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      fetchLead();
      fetchOthers();
    }
  }, [user, fetchLead, fetchOthers]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!user || !user.is_admin) return <Navigate to="/admin/login" replace />;

  const patchLeadLocal = (lid, patch) => {
    if (lid === lead?.id) setLead((cur) => (cur ? { ...cur, ...patch } : cur));
  };

  const updateLead = async (lid, patch) => {
    await axios.patch(`${API_BASE}/admin/leads/${lid}`, patch, { withCredentials: true });
    patchLeadLocal(lid, patch);
  };

  const downloadStatement = () => {
    window.open(`${API_BASE}/admin/leads/${id}/statement`, "_blank");
  };

  const touchLead = async (lid, channel) => {
    try {
      const url = channel ? `${API_BASE}/admin/leads/${lid}/touch?channel=${channel}` : `${API_BASE}/admin/leads/${lid}/touch`;
      const r = await axios.post(url, {}, { withCredentials: true });
      const ts = r.data?.last_touched_at;
      if (ts) patchLeadLocal(lid, { last_touched_at: ts });
    } catch (err) { console.warn("[lead-detail] touch failed:", err); }
  };

  const snoozeLead = async (lid) => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads/${lid}/snooze`, {}, { withCredentials: true });
      patchLeadLocal(lid, {
        snooze_until: r.data?.snooze_until,
        snoozed_by: r.data?.snoozed_by,
        snoozed_at: r.data?.snoozed_at,
        notes: r.data?.notes,
        last_touched_at: r.data?.last_touched_at,
      });
      toast.success("Snoozed for 24 hours");
    } catch {
      toast.error("Unable to snooze lead. Please try again.");
    }
  };

  const unsnoozeLead = async (lid) => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads/${lid}/unsnooze`, {}, { withCredentials: true });
      patchLeadLocal(lid, {
        snooze_until: null, snoozed_by: null, snoozed_at: null,
        notes: r.data?.notes, last_touched_at: r.data?.last_touched_at,
      });
      toast.success("Snooze cancelled");
    } catch {
      toast.error("Unable to cancel snooze. Please try again.");
    }
  };

  const markContacted = async () => {
    if (!lead) return;
    const patch = lead.status === "new" ? { status: "contacted" } : {};
    if (Object.keys(patch).length) await updateLead(lead.id, patch);
    await touchLead(lead.id, "contacted");
    toast.success("Marked as contacted");
  };

  const moveLead = async (action) => {
    const labels = {
      archive: "Archived",
      unarchive: "Unarchived",
      trash: "Moved to Trash",
      restore: "Restored from Trash",
    };
    try {
      await axios.post(`${API_BASE}/admin/leads/${id}/${action}`, {}, { withCredentials: true });
      toast.success(labels[action] || "Updated");
      // Navigate back to the leads tab after archive/trash so admin doesn't sit on a stale page.
      if (action === "trash" || action === "archive") {
        navigate("/admin#leads");
      } else {
        fetchLead();
      }
    } catch {
      toast.error("Action failed");
    }
  };

  const permanentDelete = async () => {
    if (!lead) return;
    const phrase = window.prompt(
      `Permanently delete "${lead.business_name || lead.contact_name || lead.id}"?\n\n` +
      `This cannot be undone. Type DELETE to confirm.`
    );
    if (phrase !== "DELETE") {
      toast.info("Permanent delete cancelled");
      return;
    }
    try {
      await axios.delete(`${API_BASE}/admin/leads/${lead.id}?confirm=DELETE`, { withCredentials: true });
      toast.success("Lead permanently deleted");
      navigate("/admin#leads");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleTestLead = async (next) => {
    if (!lead) return;
    try {
      await axios.post(
        `${API_BASE}/admin/leads/${lead.id}/test-flag`,
        { is_test_lead: next },
        { withCredentials: true },
      );
      patchLeadLocal(lead.id, { is_test_lead: next });
      toast.success(next ? "Marked as test lead" : "Test lead flag cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update test flag");
    }
  };

  const view = lead?.trashed ? "trash" : lead?.archived ? "archived" : "active";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-lead-detail-page" data-prerender-ready="true">
      <Seo {...getSeoForPath("/admin")} />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[72px] py-3 flex items-center justify-between gap-x-6">
          <div className="flex items-center gap-4 min-w-0">
            <Logo variant="dark" to="/admin" className="h-9 lg:h-10 w-auto select-none" linkClassName="shrink-0" />
            <div className="hidden sm:block h-8 w-px bg-slate-200" />
            <Link
              to="/admin#leads"
              data-testid="back-to-leads"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[var(--swipe-accent)] hover:text-[var(--swipe-accent)] text-slate-700 text-[13px] font-semibold"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Leads
            </Link>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium whitespace-nowrap transition-colors"
          >
            <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {fetching ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="lead-detail-loading">
            Loading lead…
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="lead-detail-error">
            <div className="text-[15px] font-bold text-[var(--swipe-navy)]">{error}</div>
            <Link
              to="/admin#leads"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--swipe-accent)] text-white text-[13px] font-semibold hover:bg-[#005ed1]"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Leads
            </Link>
          </div>
        ) : lead ? (
          <LeadDrawer
            lead={lead}
            allLeads={allLeads}
            view={view}
            mode="page"
            onClose={() => navigate("/admin#leads")}
            onUpdate={updateLead}
            onDownload={downloadStatement}
            onTouch={(channel) => touchLead(lead.id, channel)}
            onMarkContacted={markContacted}
            onSnooze={() => snoozeLead(lead.id)}
            onUnsnooze={() => unsnoozeLead(lead.id)}
            onOpenLead={(l) => navigate(`/admin/leads/${l.id}`)}
            onLeadPatched={patchLeadLocal}
            onArchive={() => moveLead("archive")}
            onUnarchive={() => moveLead("unarchive")}
            onTrash={() => moveLead("trash")}
            onRestore={() => moveLead("restore")}
            onPermanentDelete={permanentDelete}
            onToggleTestLead={toggleTestLead}
          />
        ) : null}
      </main>
    </div>
  );
}
